import { useState, useEffect, useCallback } from 'react';
import { statisticsService } from '../services/statistics';
import { useAuth } from '../contexts/AuthContext';

export const useDashboard = () => {
  const { isAuthenticated } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setIsLoading(true);
      setError(null);

      const [statsData, activitiesData] = await Promise.all([
        statisticsService.getDashboardStats(),
        statisticsService.getRecentActivities(),
      ]);

      setDashboardData(statsData);
      setRecentActivities(activitiesData);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message || 'Failed to fetch dashboard data');
      console.error('Dashboard fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Load data on component mount and when authentication changes
  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboardData();
    }
  }, [isAuthenticated, fetchDashboardData]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(fetchDashboardData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated, fetchDashboardData]);

  const refreshData = useCallback(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  return {
    dashboardData,
    recentActivities,
    isLoading,
    error,
    lastUpdated,
    refreshData,
  };
};
